import { Card, Row, Col, Input, Button, Space, Popconfirm, Typography, Divider, Switch, Tooltip, Tabs, Select, TimePicker, Tag } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, CheckOutlined, CloseOutlined, ClockCircleOutlined } from "@ant-design/icons";
import useMessage from "antd/es/message/useMessage";
import dayjs from "dayjs";
import { ConfigurationService } from "../../Services/Manage/ConfigurationService";
import { useState, useEffect } from "react";
import { EmailConfigService } from "../../Services/Manage/EmailConfigService";

const TIME_FORMAT = "hh:mm A";

const getPermission = (link) => {
    try {
        const raw = localStorage.getItem("ome_permission");
        const parsed = JSON.parse(raw);
        const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
        return permissions.find(p => p.link === link) || { add: false, edit: false, delete: false, read: false };
    } catch { return { add: false, edit: false, delete: false, read: false }; }
};

const CARDS = [
    { type: "enquiry_status", title: "Enquiry Status", showToggles: true, showLeadTemp: true },
    { type: "lead_source", title: "Lead Source", showToggles: false },
    { type: "industry_type", title: "Industry Type", showToggles: false },
    { type: "deployment_type", title: "Deployment Type", showToggles: false },
    { type: "follow_up_type", title: "Follow-up Type", showToggles: false },


];


const CARDS_EMPLOYEE = [
    { type: "territory", title: "Territory", showToggles: false },
    { type: "lead_source_focus", title: "Lead Source / Service Focus", showToggles: false },
    { type: "work_location", title: "Work Location", showToggles: false },
    { type: "shift", title: "Working Hours / Shift", showToggles: false, showTimeRange: true },
];

const CARDS_ADD_TARGET = [
    { type: "target_type", title: "Target Type", showToggles: false },
];

const CARDS_ADD_PROJECT = [
    { type: "project_type", title: "Project Type", showToggles: false },
    { type: "project_status", title: "Project Status", showToggles: false },
];

const CARDS_ADD_PROJECT_EXPENSE = [
    { type: "expense_category", title: "Expense Category", showToggles: false },
    { type: "expense_sub_category", title: "Expense Sub Category", showToggles: false },
];


const StatusCard = ({ type, title, messageApi, perm, showToggles, showLeadTemp, showTimeRange }) => {
    const [items, setItems] = useState([]);
    const [search, setSearch] = useState("");
    const [inputVal, setInputVal] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editVal, setEditVal] = useState("");
    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);
    const [editStartTime, setEditStartTime] = useState(null);
    const [editEndTime, setEditEndTime] = useState(null);

    const load = async () => {
        try {
            const res = await ConfigurationService.getByType(type);
            setItems(res.data || []);
        } catch { messageApi.error("Failed to load"); }
    };

    useEffect(() => { load(); }, []);

    const filtered = items.filter(i =>
        i.label.toLowerCase().includes(search.toLowerCase())
    );

    const handleAdd = async () => {
        if (!inputVal.trim()) return messageApi.warning("Enter a value");
        if (showTimeRange && (!startTime || !endTime)) return messageApi.warning("Select start and end time");
        try {
            const payload = { type, label: inputVal.trim() };
            if (showTimeRange) {
                payload.start_time = startTime.format(TIME_FORMAT);
                payload.end_time = endTime.format(TIME_FORMAT);
            }
            const res = await ConfigurationService.create(payload);
            if (res.status) {
                messageApi.success(res.message);
                setInputVal("");
                setStartTime(null);
                setEndTime(null);
                load();
            }
            else messageApi.error(res.message);
        } catch { messageApi.error("Failed to add"); }
    };

    const handleUpdate = async (id) => {
        if (!editVal.trim()) return messageApi.warning("Enter a value");
        if (showTimeRange && (!editStartTime || !editEndTime)) return messageApi.warning("Select start and end time");
        try {
            const payload = { label: editVal.trim() };
            if (showTimeRange) {
                payload.start_time = editStartTime.format(TIME_FORMAT);
                payload.end_time = editEndTime.format(TIME_FORMAT);
            }
            const res = await ConfigurationService.update(id, payload);
            if (res.status) { messageApi.success(res.message); setEditingId(null); load(); }
            else messageApi.error(res.message);
        } catch { messageApi.error("Failed to update"); }
    };

    const handleDelete = async (id) => {
        try {
            const res = await ConfigurationService.delete(id);
            if (res.status) { messageApi.success(res.message); load(); }
            else messageApi.error(res.message);
        } catch { messageApi.error("Failed to delete"); }
    };

    return (
        <Card
            title={<Typography.Text strong>{title}</Typography.Text>}
            extra={
                <Input
                    size="middle"
                    placeholder="Search..."
                    prefix={<SearchOutlined />}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    allowClear
                    style={{ width: 180 }}
                />
            }
            style={{ height: "100%" }}
        >
            {/* Add row */}
            <Space.Compact style={{ width: "100%", marginBottom: 10 }}>
                <Input
                    placeholder={showTimeRange ? "Shift name (e.g. Day, Night)..." : `Add ${title.toLowerCase()}...`}
                    value={inputVal}
                    onChange={e => setInputVal(e.target.value)}
                    onPressEnter={handleAdd}
                    size="middle"
                />
                <Tooltip title={!perm.add ? "You don't have permission to create" : ""}>
                    <Button type="primary" size="middle" icon={<PlusOutlined />} onClick={handleAdd} disabled={!perm.add}>
                        Add
                    </Button>
                </Tooltip>
            </Space.Compact>

            {showTimeRange && (
                <Space style={{ width: "100%", marginBottom: 10 }} size="small">
                    <div>
                        <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 2 }}>Start Time</Typography.Text>
                        <TimePicker
                            placeholder="Start time"
                            format={TIME_FORMAT}
                            value={startTime}
                            onChange={setStartTime}
                            size="middle"
                            use12Hours
                            style={{ width: 150 }}
                        />
                    </div>
                    <div>
                        <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 2 }}>End Time</Typography.Text>
                        <TimePicker
                            placeholder="End time"
                            format={TIME_FORMAT}
                            value={endTime}
                            onChange={setEndTime}
                            size="middle"
                            use12Hours
                            style={{ width: 150 }}
                        />
                    </div>
                </Space>
            )}

            <Divider style={{ margin: "0 0 8px" }} />

            {/* List */}
            <div style={{ maxHeight: 380, overflowY: "auto", overflowX: "hidden", paddingRight: 4 }}>
                {filtered.length === 0 && (
                    <div style={{ color: "var(--color-text-tertiary)", textAlign: "center", padding: "10px 0" }}>
                        No items found
                    </div>
                )}
                {filtered.map((item, idx) => (
                    <div key={item.id}>
                        {editingId === item.id ? (
                            <Space.Compact style={{ width: "100%", padding: "6px 0" }}>
                                <Input
                                    value={editVal}
                                    onChange={e => setEditVal(e.target.value)}
                                    onPressEnter={() => handleUpdate(item.id)}
                                    size="middle"
                                    autoFocus
                                />
                                <Button size="middle" type="primary" icon={<CheckOutlined />} onClick={() => handleUpdate(item.id)}>Save</Button>
                                <Button size="middle" icon={<CloseOutlined />} onClick={() => setEditingId(null)}>Cancel</Button>
                            </Space.Compact>
                        ) : null}
                        {editingId === item.id && showTimeRange && (
                            <Space style={{ width: "100%", padding: "0 0 6px" }} size="small">
                                <div>
                                    <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 2 }}>Start Time</Typography.Text>
                                    <TimePicker
                                        placeholder="Start time"
                                        format={TIME_FORMAT}
                                        value={editStartTime}
                                        onChange={setEditStartTime}
                                        size="middle"
                                        use12Hours
                                        style={{ width: 150 }}
                                    />
                                </div>
                                <div>
                                    <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 2 }}>End Time</Typography.Text>
                                    <TimePicker
                                        placeholder="End time"
                                        format={TIME_FORMAT}
                                        value={editEndTime}
                                        onChange={setEditEndTime}
                                        size="middle"
                                        use12Hours
                                        style={{ width: 150 }}
                                    />
                                </div>
                            </Space>
                        )}
                        {editingId !== item.id && (
                            <div style={{
                                display: "flex", alignItems: "center",
                                justifyContent: "space-between",
                                flexWrap: "wrap",
                                rowGap: 6,
                                padding: "5px 12px",
                                borderRadius: 6,
                                width: "100%",
                                gap: 8,
                                background: idx % 2 === 0 ? "var(--color-background-secondary)" : "transparent",
                            }}>
                                <Space size="small" style={{ minWidth: 0, flex: "1 1 auto" }}>
                                    <Typography.Text
                                        style={{ maxWidth: 90, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                                        title={item.label}
                                    >
                                        {item.label}
                                    </Typography.Text>
                                    {showTimeRange && item.start_time && item.end_time && (
                                        <Tag icon={<ClockCircleOutlined />} color="blue">
                                            {item.start_time} – {item.end_time}
                                            {(() => {
                                                const start = dayjs(item.start_time, TIME_FORMAT);
                                                let end = dayjs(item.end_time, TIME_FORMAT);
                                                if (end.isBefore(start)) end = end.add(1, "day");
                                                const minutes = end.diff(start, "minute");
                                                const hrs = Math.floor(minutes / 60);
                                                const mins = minutes % 60;
                                                const durationStr = mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
                                                return ` (${durationStr})`;
                                            })()}
                                        </Tag>
                                    )}
                                </Space>
                                <Space size="small" wrap style={{ flexShrink: 0 }}>
                                    {showToggles && (
                                        <>
                                            <Switch
                                                size="small"
                                                disabled={!perm.edit}
                                                checkedChildren="Quotation"
                                                unCheckedChildren="Quotation"
                                                checked={!!item.move_to_quotation}
                                                onChange={async (checked) => {
                                                    try {
                                                        const res = await ConfigurationService.update(item.id, { move_to_quotation: checked });
                                                        if (res.status) {
                                                            messageApi.success("Status updated");
                                                            load();
                                                        }
                                                        else messageApi.error(res.message);
                                                    } catch { messageApi.error("Failed to update"); }
                                                }}
                                            />
                                            {/* <Switch
                size="medium"
                disabled={!perm.edit}
                checkedChildren="Move to Complete"
                unCheckedChildren="Move to Complete"
                checked={!!item.move_to_complete}
                onChange={async (checked) => {
                    try {
                        const res = await ConfigurationService.update(item.id, { move_to_complete: checked });
                        if (res.status) { messageApi.success("Updated Status"); load(); }
                        else messageApi.error(res.message);
                    } catch { messageApi.error("Failed to update"); }
                }}
            /> */}
                                        </>
                                    )}
                                    {showLeadTemp && (
                                        <Select
                                            size="small"
                                            allowClear
                                            placeholder="Lead Temp"
                                            value={item.lead_temperature || null}
                                            style={{ width: 100 }}
                                            disabled={!perm.edit}
                                            options={[
                                                { value: "hot", label: "🔴 Hot" },
                                                { value: "warm", label: "🟡 Warm" },
                                                { value: "cold", label: "🔵 Cold" },
                                            ]}
                                            onChange={async (val) => {
                                                try {
                                                    const res = await ConfigurationService.update(item.id, { lead_temperature: val || null });
                                                    if (res.status) {
                                                        messageApi.success("Lead updated");
                                                        load();
                                                    }
                                                    else messageApi.error(res.message);
                                                } catch { messageApi.error("Failed to update"); }
                                            }}
                                        />
                                    )}
                                    <Tooltip title={!perm.edit ? "You don't have permission to edit" : ""}>

                                        <Button
                                            size="small"
                                            icon={<EditOutlined />}
                                            disabled={!perm.edit}
                                            onClick={() => {
                                                setEditingId(item.id);
                                                setEditVal(item.label);
                                                if (showTimeRange) {
                                                    setEditStartTime(item.start_time ? dayjs(item.start_time, TIME_FORMAT) : null);
                                                    setEditEndTime(item.end_time ? dayjs(item.end_time, TIME_FORMAT) : null);
                                                }
                                            }}
                                        />
                                    </Tooltip>
                                    <Popconfirm
                                        title="Delete this item?"
                                        okText="Yes"
                                        cancelText="No"
                                        okButtonProps={{ danger: true }}
                                        onConfirm={() => handleDelete(item.id)}
                                        disabled={!perm.delete}
                                    >
                                        <Tooltip title={!perm.delete ? "You don't have permission to delete" : ""}>
                                            <Button size="small" danger icon={<DeleteOutlined />} disabled={!perm.delete} />
                                        </Tooltip>
                                    </Popconfirm>
                                </Space>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </Card>
    );
};
const EmailSettingsCard = ({ messageApi, perm }) => {
    const [email, setEmail] = useState("");
    const [appPassword, setAppPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const res = await EmailConfigService.get();
                if (res.status && res.data) {
                    setEmail(res.data.email || "");
                    setAppPassword(res.data.app_password || "");
                }
            } catch { }
            setLoading(false);
        };
        load();
    }, []);

    const handleSave = async () => {
        if (!email.trim() || !appPassword.trim())
            return messageApi.warning("Enter both email and app password");
        setSaving(true);
        try {
            const res = await EmailConfigService.save({ email: email.trim(), app_password: appPassword.trim() });
            if (res.status) messageApi.success("Email config saved");
            else messageApi.error(res.message);
        } catch { messageApi.error("Failed to save"); }
        setSaving(false);
    };

    return (
        <Card title={<Typography.Text strong>Gmail Settings</Typography.Text>} style={{ maxWidth: 480 }}>
            <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
                Used to send Sales Target reminder emails. Use a Gmail App Password (not your regular password). Generate one at{" "}
                <Typography.Link href="https://myaccount.google.com/apppasswords" target="_blank">
                    myaccount.google.com/apppasswords
                </Typography.Link>.
            </Typography.Paragraph>
            <Space direction="vertical" style={{ width: "100%" }}>
                <div>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>Gmail Address</Typography.Text>
                    <Input placeholder="yourname@gmail.com" value={email}
                        onChange={e => setEmail(e.target.value)} disabled={loading} style={{ marginTop: 4 }} />
                </div>
                <div>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>App Password</Typography.Text>
                    <Input.Password placeholder="xxxx xxxx xxxx xxxx" value={appPassword}
                        onChange={e => setAppPassword(e.target.value)} disabled={loading} style={{ marginTop: 4 }} />
                </div>
                <Tooltip title={!perm.edit ? "No permission to edit" : ""}>
                    <Button type="primary" loading={saving} disabled={!perm.edit}
                        style={{ background: "rgba(11,93,148,0.95)" }} onClick={handleSave}>
                        Save Settings
                    </Button>
                </Tooltip>
            </Space>
        </Card>
    );
};

const Configuration = () => {
    const [messageApi, messageCtx] = useMessage();
    const perm = getPermission('/configuration');

    const tabItems = [
        {
            key: "enquiry",
            label: "Enquiry",
            children: (
                <Row gutter={[16, 16]}>
                    {CARDS.map(card => (
                        <Col xs={24} md={8} key={card.type}>
                            <StatusCard {...card} messageApi={messageApi} perm={perm} />
                        </Col>
                    ))}
                </Row>
            ),
        },
        {
            key: "employees",
            label: "Add Employees",
            children: (
                <Row gutter={[16, 16]}>
                    {CARDS_EMPLOYEE.map(card => (
                        <Col xs={24} md={8} key={card.type}>
                            <StatusCard {...card} messageApi={messageApi} perm={perm} />
                        </Col>
                    ))}
                </Row>
            ),
        },
        {
            key: "add_target",
            label: "Add Target",
            children: (
                <Row gutter={[16, 16]}>
                    {CARDS_ADD_TARGET.map(card => (
                        <Col xs={24} md={8} key={card.type}>
                            <StatusCard {...card} messageApi={messageApi} perm={perm} />
                        </Col>
                    ))}
                </Row>
            ),
        },
        {
            key: "add_project",
            label: "Add Project",
            children: (
                <Row gutter={[16, 16]}>
                    {CARDS_ADD_PROJECT.map(card => (
                        <Col xs={24} md={8} key={card.type}>
                            <StatusCard {...card} messageApi={messageApi} perm={perm} />
                        </Col>
                    ))}
                </Row>
            ),
        },
        {
            key: "add_project_expense",
            label: "Create Project Expenses",
            children: (
                <Row gutter={[16, 16]}>
                    {CARDS_ADD_PROJECT_EXPENSE.map(card => (
                        <Col xs={24} md={8} key={card.type}>
                            <StatusCard {...card} messageApi={messageApi} perm={perm} />
                        </Col>
                    ))}
                </Row>
            ),
        },
        {
            key: "email_settings",
            label: "Email Settings",
            children: <EmailSettingsCard messageApi={messageApi} perm={perm} />,
        },
    ];

    return (
        <div>
            {messageCtx}
            <Card title={<Typography.Title level={4} style={{ margin: 0 }}>Configuration</Typography.Title>}>
                <Tabs
                    tabPosition="left"
                    size="large"
                    defaultActiveKey="enquiry"
                    items={tabItems}
                />
            </Card>
        </div>
    );
};

export default Configuration;